import { useTheme } from '@mui/material/styles';
import { defineMessages, useIntl } from 'react-intl';

import { getObservationConfig } from '@constants/fhir';

import { useConversion } from '@hooks/useConversion';

import { convertUnit, getUnit } from '@utils/conversion';
import { capitalize, formatVitalSign } from '@utils/format';
import { displayFriendlyRiskLevel } from '@utils/reference_range';

import { type ParameterModel, ReferenceRangeRiskLevel } from 'generated/types';

type RefRangeParams = {
  code: string;
  parameters: ParameterModel[];
  shorthand?: boolean;
  include_clusivity?: boolean;
};

const messages = defineMessages({
  above: {
    id: 'GLOBAL.above',
    defaultMessage: 'Above',
  },
  aboveOrEqual: {
    id: 'GLOBAL.aboveOrEqual',
    defaultMessage: 'Above or equal to',
  },
  below: {
    id: 'GLOBAL.below',
    defaultMessage: 'Below',
  },
  belowOrEqual: {
    id: 'GLOBAL.belowOrEqual',
    defaultMessage: 'Below or equal to',
  },
  standard: {
    id: 'GLOBAL.parameter.standard',
    defaultMessage: 'Standard',
  },
  custom: {
    id: 'GLOBAL.parameter.custom',
    defaultMessage: 'Custom',
  },
});

export default function useReferenceRange() {
  const { formatMessage } = useIntl();
  const {
    state: { conversion },
  } = useConversion();
  const {
    palette: { background, error, info, success, text, warning },
  } = useTheme();

  const parameterMap: Record<
    ReferenceRangeRiskLevel,
    { bgColour: string; colour: string }
  > = {
    [ReferenceRangeRiskLevel.low]: {
      colour: info.main,
      bgColour: '#DAF9FE',
    },
    [ReferenceRangeRiskLevel.normal]: {
      colour: success.main,
      bgColour: '#DDFFDD',
    },
    [ReferenceRangeRiskLevel.mild]: {
      colour: warning.main,
      bgColour: '#FFF4D3',
    },
    [ReferenceRangeRiskLevel.moderate]: {
      colour: warning.dark,
      bgColour: '#FFE6D2',
    },
    [ReferenceRangeRiskLevel.high]: { colour: error.main, bgColour: '#F8D1D1' },
    [ReferenceRangeRiskLevel.crisis]: {
      colour: error.dark,
      bgColour: '#E1AFB3',
    },
    [ReferenceRangeRiskLevel.below_target]: {
      colour: text.primary,
      bgColour: background.paper,
    },
    [ReferenceRangeRiskLevel.target]: {
      colour: text.primary,
      bgColour: background.paper,
    },
    [ReferenceRangeRiskLevel.ideal]: {
      colour: text.primary,
      bgColour: background.paper,
    },
  };

  const convertRange = (
    code: string,
    unit?: string,
    precision?: number,
    val?: number
  ) => convertUnit(val, unit, conversion[code] ? 'CON' : 'SI', precision, code);

  function getFromToRange({
    code,
    parameters,
    shorthand = false,
    client_facing = false,
    include_clusivity = true,
  }: RefRangeParams & {
    client_facing?: boolean;
  }) {
    const signData = getObservationConfig(code);
    const format = signData?.format;
    const unit = signData?.unit;
    const precision = signData?.precision;

    const result = parameters.reduce<{
      from: string[];
      to: string[];
    }>(
      (acc, curr) => {
        const lowerLimit = curr.lower_bound?.value;
        const lower_bound_inclusive = curr.lower_bound?.inclusive;
        const upperLimit = curr.upper_bound?.value;
        const upper_bound_inclusive = curr.upper_bound?.inclusive;
        if (typeof lowerLimit !== 'number' && typeof upperLimit !== 'number') {
          return acc;
        }

        if (
          typeof lowerLimit === 'number' &&
          typeof upperLimit === 'number' &&
          lowerLimit === upperLimit
        ) {
          acc.from.push((lowerLimit || upperLimit).toString());
          return acc;
        }

        if (lowerLimit) {
          /**
           * If lower & upper inclusive are T: "39-41"
           * If lower inclusive is T: "Above or equal to 39"
           * If lower inclusive is F: "Above 39"
           * If lower inclusive is undefined: "39"
           */
          let inclusiveText = '';
          if (
            lowerLimit &&
            upperLimit &&
            (client_facing || !include_clusivity)
          ) {
            inclusiveText = '';
          } else if (
            typeof lower_bound_inclusive !== 'boolean' ||
            (lower_bound_inclusive && upper_bound_inclusive)
            // || (client_facing && upperLimit)
          ) {
            inclusiveText = '';
          } else if (lower_bound_inclusive) {
            inclusiveText = shorthand
              ? '>='
              : formatMessage(messages.aboveOrEqual);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          } else if (!lower_bound_inclusive) {
            inclusiveText = shorthand ? '>' : formatMessage(messages.above);
          }
          acc.from.push(
            `${inclusiveText} ${formatVitalSign(
              [Number(convertRange(code, unit, precision, lowerLimit))],
              false,
              format
            )}`
          );
        }
        if (upperLimit) {
          let inclusiveText = '';
          if (
            lowerLimit &&
            upperLimit &&
            (client_facing || !include_clusivity)
          ) {
            inclusiveText = '';
          } else if (
            typeof upper_bound_inclusive !== 'boolean' ||
            (lower_bound_inclusive && upper_bound_inclusive)
            // || (client_facing && lowerLimit)
          ) {
            inclusiveText = '';
          } else if (upper_bound_inclusive) {
            inclusiveText = shorthand
              ? '<='
              : formatMessage(messages.belowOrEqual);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          } else if (!upper_bound_inclusive) {
            inclusiveText = shorthand ? '<' : formatMessage(messages.below);
          }
          acc.to.push(
            `${inclusiveText} ${formatVitalSign(
              [Number(convertRange(code, unit, precision, upperLimit))],
              false,
              format
            )}`
          );
        }
        return acc;
      },
      { from: [], to: [] }
    );
    return result;
  }

  function displayFriendlyRange({
    /** Metric VITALL code */
    code,
    /** Metric parameters */
    parameters,
    /** Display shorthand range - False: Above or equal, True: >= */
    shorthand = false,
    /** Display metric unit */
    include_units = true,
    /** Displays cleaner ranges: >=120 - <130 as 120-130 */
    client_facing = false,
    /** Do not display (in/ex)clusivity, only numbers
     * Case 1: Has upper and lower bounds -> 4-7
     * Case 2: Has upper bound only -> Below 7
     * Case 3: Has lower bound -> Above 4
     */
    include_clusivity = true,
    /**
     * Add (Standard) or (Custom) suffix to friendly range
     * If true: It is a custom parameter -> 130-139 mmHg (Custom)
     * If false: It is a standard parameter -> 130-139 mmHg (Standard)
     * If undefined: Do not include suffix ->  130-139 mmHg
     */
    is_custom = undefined,
  }: RefRangeParams & {
    include_units?: boolean;
    client_facing?: boolean;
    is_custom?: boolean;
  }) {
    const signData = getObservationConfig(code);
    const unit = signData?.unit;
    const convertedUnit = getUnit(unit, conversion[code] ? 'CON' : 'SI', code);
    const { from, to } = getFromToRange({
      code,
      parameters,
      shorthand,
      client_facing,
      include_clusivity,
    });
    const lowerBound = from.join('/');
    const upperBound = to.join('/');

    return `${lowerBound} ${lowerBound && upperBound ? '-' : ''} ${upperBound}${
      include_units ? ` ${convertedUnit}` : ''
    } ${
      typeof is_custom === 'boolean'
        ? `(${formatMessage(is_custom ? messages.custom : messages.standard)})`
        : ''
    }`;
  }

  /**
   * Format a reference range condition by its risk, bounds, standard/custom
   * @param param0 Risk, range, standard/custom and condition code
   * @returns Friendly formatted reference range
   * E.g. Systolic High (Hypertension Stage 2): Above 140 mmHg (Standard)
   * E.g. Borderline: 6.5-11.5 mmol/L (Custom)
   */
  function formatAlertParameterLabel({
    code,
    friendly_label,
    is_custom,
    range_code,
    risk,
  }: {
    code: string;
    is_custom: boolean;
    risk: ReferenceRangeRiskLevel;
    friendly_label: string;
    range_code?: string | null;
  }) {
    const messageId = displayFriendlyRiskLevel(code, risk);
    const friendly_risk = messageId ? formatMessage({ id: messageId }) : '';
    return `${
      range_code ? `${capitalize(range_code)}` : ''
    } ${friendly_risk}: ${friendly_label} (${formatMessage({
      id: is_custom ? 'GLOBAL.range.custom' : 'GLOBAL.range.standard',
    })})`;
  }

  return { displayFriendlyRange, formatAlertParameterLabel, parameterMap };
}
